import React from 'react';
import {
  ChartsIcon,
  MercIcon,
  SettingsIcon,
  ReportIcon,
} from '../../../../theme/icons';
import {
  Badge,
  Box,
  Button,
  IconButton,
  styled,
  useMediaQuery,
  useTheme,
} from '@mui/material';
import { AlarmIcon } from '../../../../../../theme/icons';
import {
  DefaultMenuButtons,
  MenuItemPrimary,
} from '../../../../../../theme/components';
import { getPolyglot } from '../../../../../../i18n';
import { ViewKey } from '../../MerchandiserContainer';
import { useAppSelector } from '../../../../../../redux/store.model';
import { getAlarmActiveBySelector } from '../../../../../../redux/incident/selectors/getAlarmActiveBySelector';
import { getFeatureToggle } from '../../../../../../featureToggle';

const useButtons = ({
  onClick,
  merchandiserId,
}: {
  onClick: React.Dispatch<React.SetStateAction<ViewKey>>;
  merchandiserId: string | undefined;
}) => {
  const { data: alarmsActive } = useAppSelector((state) =>
    getAlarmActiveBySelector(state, {
      groupId: merchandiserId,
    })
  );
  const featureToggle = useAppSelector(getFeatureToggle);

  const displayReports = featureToggle.reports;
  return [
    // { icon: <DashBoardIcon />, view: 'dashboard' , onClick: () => navigate('dashboard')},
    {
      icon: <MercIcon />,
      view: 'detail',
      onClick: () => onClick('detail'),
    },
    {
      icon: <SettingsIcon />,
      view: 'settings',
      onClick: () => onClick('settings'),
    },
    {
      icon: <ChartsIcon />,
      view: 'history',
      onClick: () => onClick('history'),
    },
    displayReports && {
      icon: <ReportIcon />,
      view: 'reports',
      onClick: () => onClick('reports'),
    },
    {
      icon: (
        <Badge badgeContent={alarmsActive?.length} color="error">
          <AlarmIcon />
        </Badge>
      ),
      view: 'alarms',
      onClick: () => onClick('alarms'),
    },
  ] as { icon: React.ReactNode; view: ViewKey; onClick: () => void }[];
};
const useAlarmButton = ({
  onClick,
  merchandiserId,
}: {
  onClick: React.Dispatch<React.SetStateAction<ViewKey>>;
  merchandiserId: string | undefined;
}) => {
  const { data: alarmsActive } = useAppSelector((state) =>
    getAlarmActiveBySelector(state, {
      groupId: merchandiserId,
    })
  );

  let alarmIcon: JSX.Element | null = (
    <Badge badgeContent={alarmsActive?.length} color="error">
      <AlarmIcon />
    </Badge>
  );

  if (alarmsActive?.length === 0) {
    alarmIcon = null;
  }
  return [
    {
      icon: alarmIcon,
      view: 'alarms',
      onClick: () => onClick('alarms'),
    },
  ] as { icon: React.ReactNode | null; view: ViewKey; onClick: () => void }[];
};

const StyledIconButton = styled(IconButton)<{ active: boolean }>(
  ({ theme, active }) => `
      background-color: ${
        active
          ? theme.palette.secondary.main
          : theme.palette.primary.contrastText
      };
      border-radius: 1rem;
      padding: 1rem;
      height: 3.5rem;
      margin: 0.5rem;
      width: 4rem;
      &:hover {
        background-color: ${theme.palette.primary.light};
      }
      `
);

interface MerchandiserActionsProps {
  moduleSelected?: string;
  onClick: React.Dispatch<React.SetStateAction<ViewKey>>;
  merchandiserId: string | undefined;
  diplayJustAlarms?: boolean;
}
function MerchandiserActions(props: MerchandiserActionsProps) {
  const { moduleSelected, onClick, merchandiserId, diplayJustAlarms } = props;

  const buttons = diplayJustAlarms
    ? useAlarmButton({ onClick, merchandiserId })
    : useButtons({ onClick, merchandiserId });

  const polyglot = getPolyglot();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));

  return (
    <Box
      sx={() => ({
        display: 'flex',
        alignContent: 'center',
        justifyContent: 'flex-end',
        fontSize: '1.8em',
        color: (theme) => theme.typography.body1.color,
      })}
      onClick={(e) => e.stopPropagation()}
      onMouseDown={(e) => e.stopPropagation()}
      onMouseEnter={(e) => e.stopPropagation()}
      onMouseLeave={(e) => e.stopPropagation()}
    >
      {isMobile ? (
        <DefaultMenuButtons
          buttons={buttons.map((button) =>
            button ? (
              <MenuItemPrimary
                key={button.view}
                onClick={button.onClick}
                selected={moduleSelected === button.view}
                color={'primary'}
              >
                <Button
                  variant="text"
                  size={'small'}
                  endIcon={button.icon}
                ></Button>
                {polyglot.t(`freshwatch.merchandiser.menu.${button.view}`)}
              </MenuItemPrimary>
            ) : null
          )}
        ></DefaultMenuButtons>
      ) : (
        buttons.map((button) => {
          return button ? (
            button.icon ? (
              <StyledIconButton
                sx={{ boxShadow: 2 }}
                key={button.view}
                active={button.view === moduleSelected}
                onClick={button.onClick}
                color="primary"
              >
                {button.icon}
              </StyledIconButton>
            ) : (
              <div style={{ width: '4rem', margin: '0.5rem' }}></div>
            )
          ) : null;
        })
      )}
    </Box>
  );
}

export default MerchandiserActions;
